import React, { useState, useEffect } from "react"
import { graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { useHubspotForm } from '@aaronhayes/react-use-hubspot-form';

import Layout from "../components/layout"
import Seo from "../components/seo"
import Hero from "../components/hero"
import Container from "../components/container"

import "./seminar-post.css"
import LinkButton from "../components/link-button"

const SeminarPostTemplate = ({ data, location }) => {

  // カスタムイベントをトリガーするための関数を作成
  const triggerHubSpotFormDisplayed = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'hubspotFormDisplayed' });
  };

  useHubspotForm({
    portalId: '39601632', // HubspotのポータルIDを指定
    formId: data.markdownRemark.frontmatter.HubSpotFormId, // Hubspotで作成したフォームのIDを指定
    target: '#SeminarForm', // これはフォームが埋め込まれるDOM要素のID
    onFormReady: triggerHubSpotFormDisplayed // HubSpotフォームが表示された時に関数を呼び出す
  });

  const post = data.markdownRemark
  const mainImage = post.frontmatter.cover
    ? getImage(post.frontmatter.cover)
    : null
  const [isOpen, setSeminarState] = useState(false)
  const seminarDate = new Date(Date.parse(post.frontmatter.date))

  useEffect(() => {
    const closeDate = new Date(Date.parse(post.frontmatter.date))
    closeDate.setMinutes(closeDate.getMinutes() - 30)
    const today = new Date()
    setSeminarState(closeDate > today)
  }, [post.frontmatter.date])

  return (
    <Layout location={location}>
      <Seo
        title={post.frontmatter.title}
        description={post.frontmatter.description || post.excerpt}
        image={
          post.frontmatter.cover
            ? post.frontmatter.cover.childImageSharp.fixed.src
            : null
        }
      />
      <Hero type="secondary">
        <div className="lg:w-9/12 lg:m-auto pt-8 lg:pt-32 pb-12 lg:pb-28 text-center">
          {isOpen ? (
            <span className="text-white bg-blue-400 rounded-full text-sm font-bold py-1 px-2 mb-5 inline-block">
              受付中
            </span>
          ) : (
            <span className="text-white bg-textGray-60 rounded-full text-sm font-bold py-1 px-2 mb-5 inline-block">
              受付終了
            </span>
          )}
          <h1 className="font-bold text-base lg:text-2xl mb-2 lg:mb-5">
            {post.frontmatter.title}
          </h1>
        </div>
      </Hero>
      <div className="relative z-30">
        <Container>
          <div className="lg:w-9/12 lg:mx-auto -mt-7 lg:-mt-11">
            {mainImage ? (
              <GatsbyImage
                image={mainImage}
                alt={post.frontmatter.title}
                className="rounded-lg shadow-xl"
              />
            ) : null}
          </div>
          <div className="text-center pt-6 pb-10 lg:p-14">
            {isOpen ? (
              <LinkButton
                text="お申し込みはこちら"
                url="#contact"
                type="primary"
              />
            ) : (
              <div className="font-bold text-pink-600 text-xs lg:text-base text-center">
                受付は終了しました。ご参加ありがとうございました。
                {post.frontmatter.reportUrl && (
                  <div className="text-center mt-8">
                    <LinkButton
                      text="セミナーレポートはこちら"
                      url={post.frontmatter.reportUrl}
                      type="primary"
                      isExternal={true}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </Container>
      </div>
      <article className="seminar-post">
        <div
          dangerouslySetInnerHTML={{ __html: post.html }}
          itemProp="articleBody"
        />
        {post.frontmatter.sponsored && (
          <div>
            {post.frontmatter.timetable && (
              <section className="event-block py-12 lg:py-14">
                <Container>
                  <div className="lg:w-9/12 lg:mx-auto">
                    <h2 className="font-bold text-xl lg:text-3xl text-blue-950">
                      タイムテーブル
                    </h2>
                    <table className="time-table">
                      <tbody>
                        {post.frontmatter.timetable.map((item, idx) => (
                          <tr key={`time-${idx}`}>
                            <th>{item.time}</th>
                            <td>
                              <div className="font-bold text-blue-950 tracking-wider">
                                {item.title}
                              </div>
                              {item.presenter && (
                                <div className="text-xs leading-loose tracking-wide py-2 border-b border-textGray-10 mb-3">
                                  {item.presenter}
                                </div>
                              )}
                              {item.description && (
                                <div className="text-xs lg:text-sm leading-relaxed lg:leading-loose tracking-wide">
                                  {item.description}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Container>
              </section>
            )}
            {post.frontmatter.introduction && (
              <section className="event-block py-12 lg:py-14">
                <Container>
                  <div className="lg:w-9/12 lg:mx-auto">
                    <h2 className="font-bold text-xl lg:text-3xl text-blue-950">
                      登壇者紹介
                    </h2>
                    {post.frontmatter.introduction.map((item, idx) => (
                      <div
                        className="introduction mb-7 lg:mb-10"
                        key={`intoroduction-${idx}`}
                      >
                        {item.presenters.map(presenter => (
                          <div
                            className="flex"
                            key={presenter.name}
                          >
                            <div className="w-20 lg:w-48 mr-4 lg:mr-6">
                              {presenter.photo && (
                                <GatsbyImage
                                  image={getImage(presenter.photo)}
                                  alt={presenter.name}
                                  className="rounded-lg w-20 lg:w-48"
                                />
                              )}
                            </div>
                            <div>
                              <div className="text-textGray-100 text-xs lg:text-sm mb-2 lg:mb-3">
                                {presenter.title}
                              </div>
                              <div className="font-bold text-blue-950 text-lg lg:text-2xl tracking-widest mb-2 lg:mb-3">
                                {presenter.name}
                              </div>
                              <div className="text-textGray-100 text-xs lg:text-sm leading-relaxed lg:leading-relaxed">
                                {presenter.description}
                              </div>
                            </div>
                          </div>
                        ))}
                        {item.company && (
                          <div className="p-3 lg:p-5 rounded-lg border border-textGray-10 mt-4 lg:mt-6">
                            {item.company.name && (
                              <div className="pb-1 font-bold text-blue-950 text-base lg:text-xl mb-1 lg:mb-2">
                                {item.company.name}
                              </div>
                            )}
                            {item.company.description && (
                              <div className="text-textGray-100 text-xs lg:text-sm leading-relaxed lg:leading-relaxed">
                                {item.company.description}
                              </div>
                            )}
                            {item.company.url && (
                              <a
                                href={item.company.url}
                                rel="noreferrer"
                                target="_blank"
                                className="text-xs lg:text-sm"
                              >
                                {item.company.url}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Container>
              </section>
            )}
          </div>
        )}
        <section
          className={`py-12 ${post.frontmatter.sponsored && "bg-gray-25"}`}
        >
          <Container>
            <div className="lg:w-9/12 lg:mx-auto">
              <h2 className="font-bold text-xl lg:text-3xl text-blue-950">
                概要
              </h2>
              <table>
                <tbody>
                  <tr>
                    {post.frontmatter.type === "seminar" ? (
                      <th>セミナー名</th>
                      ) : (
                      <th>動画名</th>
                      )
                    }
                    <td>{post.frontmatter.title}</td>
                  </tr>
                    {post.frontmatter.type === "seminar" && (
                      <tr>
                        <th>開催日時</th>
                        <td>
                          {seminarDate.getFullYear()}年{seminarDate.getMonth() + 1}
                          月{seminarDate.getDate()}日（
                          {
                            ["日", "月", "火", "水", "木", "金", "土"][
                              seminarDate.getDay()
                            ]
                          }
                          ）{post.frontmatter.term}
                        </td>
                      </tr>
                      )
                    }
                  <tr>
                    <th>会場</th>
                    <td>{post.frontmatter.place}</td>
                  </tr>
                  <tr>
                    <th>主催</th>
                    <td>{post.frontmatter.organizer}</td>
                  </tr>
                  <tr>
                    <th>費用</th>
                    <td>{post.frontmatter.price}</td>
                  </tr>
                  <tr>
                    {post.frontmatter.type === "seminar" ? (
                      <th>参加方法</th>
                      ) : (
                      <th>視聴方法</th>
                      )
                      }
                      {post.frontmatter.type === "seminar" ? (
                        <td>下記フォームよりお申し込みください。参加方法は前日までにメールでご案内します。</td>
                        ) : (
                        <td>下記フォームよりお申込みください。動画URLをメールでご案内します。</td>
                        )
                      }
                  </tr>
                  <tr>
                    <th>注意事項</th>
                    <td>
                      {post.frontmatter.type === "seminar" ? (
                        <ul>
                          <li>
                            フリーメールアドレスはお使いいただけません。
                          </li>
                          <li>
                            所属企業が確認できない方、競合他社さまの場合は参加をお断りさせていただくことがございます。
                          </li>
                          <li>
                            お一人ずつのお申し込みをお願いします。
                          </li>
                          <li>
                            オンライン配信サービスの接続や設定は参加者さまご自身でご用意いただきます。
                          </li>
                          <li>
                            インターネット接続環境によって映像や音声が乱れる場合がございますのでご了承ください。
                          </li>
                          <li>
                            配信内容の録画・録音をさせていただく場合がございます。参加者様の録画や録音、撮影はご遠慮ください。
                          </li>
                        </ul>
                      ) : (
                        <ul>
                          <li>
                            予告なく申し込みを締め切る・動画を削除する可能性がありますので、あらかじめご了承ください。
                          </li>
                          <li>
                            フリーメールアドレスはお使いいただけません。
                          </li>
                          <li>
                            所属企業が確認できない方、競合他社様の場合は参加をお断りさせていただくことがございます。
                          </li>
                          <li>
                            1社から複数名ご参加の場合は、お一人様ずつのお申し込みをお願いします。
                          </li>
                          <li>
                            オンライン配信サービスの接続や設定については参加者様ご自身でご用意いただきます。
                          </li>
                          <li>
                            インターネット接続環境によって映像や音声が乱れる場合がございますのでご了承ください。
                          </li>
                        </ul>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Container>
        </section>
      </article>

      <section className="py-12 lg:py-14 bg-blue-50" id="contact">
        <Container>
          <div className="lg:w-9/12 lg:mx-auto">
            <h2 className="font-bold text-xl lg:text-3xl text-blue-950 pb-5 lg:pb-10">
              お申し込みフォーム
            </h2>
            <div className="bg-white rounded-lg p-1 lg:p-10">
              <div>
                {isOpen ? (
                  <div id="SeminarForm"></div>
                ) : (
                  <div className="p-5 lg:p-8 text-textGray-100 text-sm lg:text-base text-center">
                    受付は終了しました
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>
    </Layout>
  )
}

export default SeminarPostTemplate

export const pageQuery = graphql`
  query BlogPostBySlug($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      html
      frontmatter {
        title
        date
        type
        description
        cover {
          childImageSharp {
            gatsbyImageData(width: 1280)
            fixed(width: 1200) {
              src
            }
          }
        }
        sponsored
        HubSpotFormId
        reportUrl
        organizer
        place
        price
        term
        apply
        introduction {
          company {
            description
            name
            url
          }
          presenters {
            description
            name
            title
            photo {
              childImageSharp {
                gatsbyImageData(width: 400)
              }
            }
          }
        }
        timetable {
          description
          presenter
          time
          title
        }
      }
    }
  }
`
